import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  deleteDoc,
  addDoc,
  serverTimestamp,
  Timestamp,
  DocumentData,
  orderBy,
} from "firebase/firestore";
import { db } from "../config/firebase";
import { Course, CourseContent, CourseType } from "@/types/courseTypes";
import { createNotification, notifyNewEnrollment, notifyInstructorAssignment, notifyStudentInstructorAssigned } from "./notificationService";

export interface Enrollment {
  id?: string;
  userId: string;
  studentId?: string;
  studentName: string;
  studentEmail: string;
  studentPhone?: string;
  studentAddress?: string;
  studentPostcode?: string;
  studentGearbox?: string;
  studentLicenseNumber?: string;
  studentEmergencyContact?: string;
  studentEmergencyPhone?: string;
  courseId: string;
  courseName: string;
  courseType: CourseType;
  instructorId?: string;
  instructorEmail?: string;
  instructorName?: string;
  status: 'pending' | 'assigned' | 'completed' | 'cancelled';
  enrollmentStatus: 'pending' | 'active' | 'completed' | 'cancelled' | 'enrolled';
  notes?: string;
  enrolledAt?: string;
  updatedAt?: string;
  paymentStatus?: string;
  paymentToInstructor?: 'unpaid' | 'paid';
  createdAt?: string;
  startDate: string;
  additionalNotes?: string;
  preferredTime: string;
  drivingExperience: string;
  totalHours?: number;
  hoursTaken?: number;
  hoursRemaining?: number;
  amount?: number;
  currency?: string;
  vehicleType?: string;
  licenseType?: string;
  previousLessons?: string;
  medicalConditions?: boolean;
  medicalDetails?: string;
  nervousAboutDriving?: boolean;
  specificGoals?: string;
  focusAreas?: string;
  upcomingSchedules?: Array<{
    date: string;
    time: string;
    status: string;
  }>;
  isPartialPayment: boolean;
  amountPaid: number;
  fullCoursePrice: number;
  remainingHours: number;
  cancellationReason?: string;
}

export interface Payment {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  amount: number;
  currency: string;
  status: string;
  createdAt: string;
  description: string;
  courseId: string;
  courseName: string;
  enrollmentId: string;
  paymentMethod: string;
  paymentType: string;
  lessonType: string;
  instructorId: string;
  instructorName: string;
  instructorEmail: string;
  hours: number;
  hoursRemaining: number;
  hoursTaken: number;
}

/**
 * Fall back to localStorage in demo mode or if Firestore isn't initialized
 */
const isUsingLocalStorage = () => {
  return !db;
};

/**
 * Create a new course in Firestore
 */
export const createCourse = async (course: Omit<Course, 'id'>): Promise<Course> => {
  if (isUsingLocalStorage()) {
    console.warn("Using localStorage for course creation (demo mode or Firestore not initialized)");
    const courseWithId = {
      ...course,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    const courses = JSON.parse(localStorage.getItem("courses") || "[]");
    localStorage.setItem("courses", JSON.stringify([...courses, courseWithId]));
    
    // Create notification
    const notification = {
      id: crypto.randomUUID(),
      type: "course-created",
      title: "New Course Created",
      message: `The course "${courseWithId.title}" has been created successfully.`,
      read: false,
      createdAt: new Date().toISOString(),
    };
    
    const notifications = JSON.parse(localStorage.getItem("adminNotifications") || "[]");
    localStorage.setItem("adminNotifications", JSON.stringify([notification, ...notifications]));
    
    return courseWithId;
  }
  
  try {
    // Create a reference to the new document
    const courseRef = doc(collection(db, "courses"));
    
    // Add timestamps
    const timestamp = new Date().toISOString();
    
    // Prepare the course object with an ID
    const courseWithId: Course = {
      ...course,
      id: courseRef.id,
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    
    // Set the document data
    await setDoc(courseRef, courseWithId);
    
    // Create a notification for the admin
    createNotification(
      'admin',
      'course-created',
      'New Course Created',
      `The course "${courseWithId.title}" has been created successfully.`
    );
    
    console.log(`Course created with ID: ${courseRef.id}`);
    return courseWithId;
  } catch (error) {
    console.error("Error creating course:", error);
    throw error;
  }
};

/**
 * Get all courses from Firestore
 */
export const getAllCourses = async (): Promise<Course[]> => {
  if (isUsingLocalStorage()) {
    console.warn("Using localStorage for course retrieval (demo mode or Firestore not initialized)");
    const storedCourses = localStorage.getItem("courses");
    if (storedCourses) {
      return JSON.parse(storedCourses);
    }
    
    // Import sample data from types if needed
    const { sampleTheoryCourses, samplePracticalCourses, sampleIndividualCourses } = await import('@/types/courseTypes');
    const combinedSampleCourses = [...sampleTheoryCourses, ...samplePracticalCourses, ...sampleIndividualCourses];
    localStorage.setItem("courses", JSON.stringify(combinedSampleCourses));
    return combinedSampleCourses;
  }
  
  try {
    const coursesRef = collection(db, "courses");
    const querySnapshot = await getDocs(coursesRef);
    
    const courses: Course[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data() as Course;
      courses.push({
        ...data,
        id: doc.id,
      });
    });
    
    return courses;
  } catch (error) {
    console.error("Error getting courses:", error);
    throw error;
  }
};

/**
 * Get a specific course by ID
 */
export const getCourseById = async (courseId: string): Promise<Course | null> => {
  if (isUsingLocalStorage()) {
    console.warn("Using localStorage for course retrieval (demo mode or Firestore not initialized)");
    const courses = JSON.parse(localStorage.getItem("courses") || "[]");
    return courses.find((c: Course) => c.id === courseId) || null;
  }
  
  try {
    const courseRef = doc(db, "courses", courseId);
    const courseSnap = await getDoc(courseRef);
    
    if (courseSnap.exists()) {
      return { id: courseSnap.id, ...courseSnap.data() } as Course;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting course:", error);
    throw error;
  }
};

/**
 * Update an existing course
 */
export const updateCourse = async (courseId: string, courseData: Partial<Course>): Promise<void> => {
  if (isUsingLocalStorage()) {
    console.warn("Using localStorage for course update (demo mode or Firestore not initialized)");
    const courses = JSON.parse(localStorage.getItem("courses") || "[]");
    const updatedCourses = courses.map((course: Course) => 
      course.id === courseId 
        ? { ...course, ...courseData, updatedAt: new Date().toISOString() } 
        : course
    );
    localStorage.setItem("courses", JSON.stringify(updatedCourses));
    return;
  }
  
  try {
    const courseRef = doc(db, "courses", courseId);
    
    // Update the timestamp
    const updateData = {
      ...courseData,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(courseRef, updateData);
    console.log(`Course ${courseId} updated successfully`);
  } catch (error) {
    console.error("Error updating course:", error);
    throw error;
  }
};

/**
 * Delete a course
 */
export const deleteCourse = async (courseId: string): Promise<void> => {
  if (isUsingLocalStorage()) {
    console.warn("Using localStorage for course deletion (demo mode or Firestore not initialized)");
    const courses = JSON.parse(localStorage.getItem("courses") || "[]");
    const updatedCourses = courses.filter((course: Course) => course.id !== courseId);
    localStorage.setItem("courses", JSON.stringify(updatedCourses));
    return;
  }
  
  try {
    await deleteDoc(doc(db, "courses", courseId));
    console.log(`Course ${courseId} deleted successfully`);
  } catch (error) {
    console.error("Error deleting course:", error);
    throw error;
  }
};

/**
 * Add content to a course
 */
export const addContentToCourse = async (
  courseId: string, 
  content: Omit<CourseContent, 'id' | 'createdAt' | 'updatedAt'>
): Promise<CourseContent> => {
  if (isUsingLocalStorage()) {
    console.warn("Using localStorage for content addition (demo mode or Firestore not initialized)");
    
    const newContent: CourseContent = {
      ...content,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    const courses = JSON.parse(localStorage.getItem("courses") || "[]");
    const updatedCourses = courses.map((course: Course) => 
      course.id === courseId 
        ? { 
            ...course, 
            contents: [...(course.contents || []), newContent],
            updatedAt: new Date().toISOString() 
          } 
        : course
    );
    
    localStorage.setItem("courses", JSON.stringify(updatedCourses));
    return newContent;
  }
  
  try {
    // Get the course
    const courseRef = doc(db, "courses", courseId);
    const courseSnap = await getDoc(courseRef);
    
    if (!courseSnap.exists()) {
      throw new Error(`Course with ID ${courseId} not found`);
    }
    
    const course = courseSnap.data() as Course;
    
    // Create new content item
    const timestamp = new Date().toISOString();
    const contentItem: CourseContent = {
      ...content,
      id: crypto.randomUUID(), // Create a unique ID for the content
      createdAt: timestamp,
      updatedAt: timestamp,
    };
    
    // Add content to the course's contents array
    const updatedContents = [...(course.contents || []), contentItem];
    
    // Update the course
    await updateDoc(courseRef, {
      contents: updatedContents,
      updatedAt: timestamp
    });
    
    console.log(`Content added to course ${courseId}`);
    return contentItem;
  } catch (error) {
    console.error("Error adding content to course:", error);
    throw error;
  }
};

/**
 * Delete content from a course
 */
export const deleteContentFromCourse = async (courseId: string, contentId: string): Promise<void> => {
  if (isUsingLocalStorage()) {
    console.warn("Using localStorage for content deletion (demo mode or Firestore not initialized)");
    
    const courses = JSON.parse(localStorage.getItem("courses") || "[]");
    const updatedCourses = courses.map((course: Course) => 
      course.id === courseId 
        ? { 
            ...course, 
            contents: (course.contents || []).filter(content => content.id !== contentId),
            updatedAt: new Date().toISOString()
          } 
        : course
    );
    
    localStorage.setItem("courses", JSON.stringify(updatedCourses));
    return;
  }
  
  try {
    // Get the course
    const courseRef = doc(db, "courses", courseId);
    const courseSnap = await getDoc(courseRef);
    
    if (!courseSnap.exists()) {
      throw new Error(`Course with ID ${courseId} not found`);
    }
    
    const course = courseSnap.data() as Course;
    
    // Filter out the content to delete
    const updatedContents = (course.contents || []).filter(
      content => content.id !== contentId
    );
    
    // Update the course
    await updateDoc(courseRef, {
      contents: updatedContents,
      updatedAt: new Date().toISOString()
    });
    
    console.log(`Content ${contentId} deleted from course ${courseId}`);
  } catch (error) {
    console.error("Error deleting content from course:", error);
    throw error;
  }
};

/**
 * Create a new enrollment
 */
export const createEnrollment = async (enrollmentData: Omit<Enrollment, 'id' | 'enrolledAt' | 'updatedAt'>): Promise<Enrollment> => {
  if (!db) throw new Error("Firestore is not initialized");

  try {
    // Check if this is a Car Hire course
    const isCarHire = enrollmentData.courseName.toLowerCase().includes("car hire");
    
    // First, get the student's user data to check for preferred instructor
    let studentPreferredInstructor: { id: string; name: string; email: string } | null = null;
    try {
      const userDoc = await getDoc(doc(db, 'users', enrollmentData.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        if (userData.instructorId && userData.instructorId.trim() !== '') {
          // Get instructor details
          const instructorDoc = await getDoc(doc(db, 'users', userData.instructorId));
          if (instructorDoc.exists()) {
            const instructorData = instructorDoc.data();
            studentPreferredInstructor = {
              id: instructorData.id,
              name: `${instructorData.firstName} ${instructorData.lastName}`,
              email: instructorData.email
            };
            console.log("Found student's preferred instructor:", studentPreferredInstructor);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching student's preferred instructor:", error);
    }
    
    // Check for existing enrollments in Firestore
    const existingEnrollmentsQuery = query(
      collection(db, 'enrollments'),
      where('userId', '==', enrollmentData.userId),
      where('status', 'in', ['active', 'pending', 'enrolled', 'assigned'])
    );

    const existingEnrollmentsSnapshot = await getDocs(existingEnrollmentsQuery);
    let existingCarHireEnrollment: Enrollment | null = null;
    let existingInstructor: { id: string; name: string; email: string } | null = null;
    let existingIndividualEnrollment: Enrollment | null = null;

    // Check for existing instructor and individual/log lessons
    if (existingEnrollmentsSnapshot.docs.length > 0) {
      const enrollments = existingEnrollmentsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Enrollment));
      
      // Find existing instructor from previous enrollments
      const existingEnrollmentWithInstructor = enrollments.find(
        enrollment => enrollment.instructorId && enrollment.instructorName && enrollment.instructorEmail
      );

      if (existingEnrollmentWithInstructor) {
        existingInstructor = {
          id: existingEnrollmentWithInstructor.instructorId,
          name: existingEnrollmentWithInstructor.instructorName || '',
          email: existingEnrollmentWithInstructor.instructorEmail || ''
        };
      }

      // Check for existing individual/log lesson enrollment or Combined Driving Lesson
      if (enrollmentData.courseType === "individual" || 
          enrollmentData.courseName.toLowerCase().includes("blog") || 
          enrollmentData.courseName.toLowerCase().includes("log")) {
        existingIndividualEnrollment = enrollments.find(enrollment => 
          enrollment.courseName === "Combined Driving Lesson" || // First check for Combined Driving Lesson
          enrollment.courseType === "individual" || // Then check for individual type
          (enrollment.courseName && 
           (enrollment.courseName.toLowerCase().includes("blog") || 
            enrollment.courseName.toLowerCase().includes("log")))
        );
      }

      // For Car Hire courses, look for an existing Car Hire enrollment
      if (isCarHire) {
        existingCarHireEnrollment = enrollments.find(
          enrollment => enrollment.courseName.toLowerCase().includes("car hire")
        );
      }
    }

    // Determine which instructor to assign
    // Priority: 1. Student's preferred instructor, 2. Existing instructor from other enrollments
    const instructorToAssign = studentPreferredInstructor || existingInstructor;

    // If this is an individual/log lesson and there's an existing one, merge them
    if (existingIndividualEnrollment && 
        (enrollmentData.courseType === "individual" || 
         enrollmentData.courseName.toLowerCase().includes("blog") || 
         enrollmentData.courseName.toLowerCase().includes("log"))) {
      console.log("Found existing individual/log lesson enrollment:", existingIndividualEnrollment);

      // Calculate new total hours and amount
      const newTotalHours = (existingIndividualEnrollment.totalHours || 0) + (enrollmentData.totalHours || 0);
      const newHoursRemaining = (existingIndividualEnrollment.hoursRemaining || 0) + (enrollmentData.totalHours || 0);
      const newAmount = (existingIndividualEnrollment.amount || 0) + (enrollmentData.amount || 0);

      // Update the existing enrollment
      const updatedEnrollment: Enrollment = {
        ...existingIndividualEnrollment,
        courseName: "Combined Driving Lesson",
        totalHours: newTotalHours,
        hoursRemaining: newHoursRemaining,
        amount: newAmount,
        updatedAt: new Date().toISOString()
      };

      // Convert to plain object for Firestore update
      const updateData = {
        courseName: "Combined Driving Lesson",
        totalHours: newTotalHours,
        hoursRemaining: newHoursRemaining,
        amount: newAmount,
        updatedAt: new Date().toISOString()
      };

      // Update the enrollment in Firestore
      const enrollmentRef = doc(db, 'enrollments', existingIndividualEnrollment.id);
      await updateDoc(enrollmentRef, updateData);

      console.log("Updated Combined Driving Lesson enrollment:", updatedEnrollment);
      return updatedEnrollment;
    }

    // If this is a Car Hire course and there's an existing Car Hire enrollment
    if (isCarHire && existingCarHireEnrollment) {
      console.log("Found existing Car Hire enrollment:", existingCarHireEnrollment);

      // Calculate new total hours and amount
      const newTotalHours = (existingCarHireEnrollment.totalHours || 0) + 3; // Add 3 hours
      const newHoursRemaining = (existingCarHireEnrollment.hoursRemaining || 0) + 3;
      const pricePerHour = enrollmentData.amount / 3; // Calculate price per hour
      const newAmount = pricePerHour * newTotalHours;

        // Update the existing enrollment
        const updatedEnrollment: Enrollment = {
        ...existingCarHireEnrollment,
          totalHours: newTotalHours,
          hoursRemaining: newHoursRemaining,
          amount: newAmount,
        updatedAt: new Date().toISOString()
      };

      // Convert to plain object for Firestore update
      const updateData = {
        totalHours: newTotalHours,
        hoursRemaining: newHoursRemaining,
        amount: newAmount,
        updatedAt: new Date().toISOString()
        };

        // Update the enrollment in Firestore
      const enrollmentRef = doc(db, 'enrollments', existingCarHireEnrollment.id);
      await updateDoc(enrollmentRef, updateData);

      console.log("Updated Car Hire enrollment:", updatedEnrollment);
      return updatedEnrollment;
    }

    // For non-Car Hire courses or new Car Hire enrollments
    const enrollmentRef = doc(collection(db, 'enrollments'));
    const now = new Date().toISOString();

    // Force 3 hours for new Car Hire enrollments
    if (isCarHire) {
      enrollmentData.totalHours = 3;
      enrollmentData.hoursRemaining = 3;
      enrollmentData.amount = (enrollmentData.amount || 0) / (enrollmentData.totalHours || 1) * 3;
    }

    // If there's an instructor to assign (preferred or existing), assign them to the new enrollment
    if (instructorToAssign) {
      enrollmentData.instructorId = instructorToAssign.id;
      enrollmentData.instructorName = instructorToAssign.name;
      enrollmentData.instructorEmail = instructorToAssign.email;
      enrollmentData.status = 'assigned';
      enrollmentData.enrollmentStatus = 'enrolled';

      const newEnrollment: Enrollment = {
        id: enrollmentRef.id,
        ...enrollmentData,
        status: 'assigned',
        enrollmentStatus: 'enrolled',
        enrolledAt: now,
        updatedAt: now
      };

      await setDoc(enrollmentRef, newEnrollment);
      console.log("Created new enrollment with assigned instructor:", newEnrollment);

      // Create chat thread if it doesn't exist
      const chatThreadsRef = collection(db, "chatThreads");
      const existingChatQuery = query(
        chatThreadsRef,
        where("participants.student.id", "==", newEnrollment.userId),
        where("participants.instructor.id", "==", instructorToAssign.id)
      );
      const existingChatSnapshot = await getDocs(existingChatQuery);

      if (existingChatSnapshot.empty) {
        const chatThread = {
          participants: {
            student: {
              id: newEnrollment.userId,
              name: newEnrollment.studentName
            },
            instructor: {
              id: instructorToAssign.id,
              name: instructorToAssign.name
            }
          },
          enrollmentId: newEnrollment.id,
          lastMessageTime: new Date().toISOString(),
          unreadCount: 0
        };
        
        await addDoc(chatThreadsRef, chatThread);
      }

      return newEnrollment;
    } else {
      // No instructor to assign, create new enrollment with pending status
      const newEnrollment: Enrollment = {
        id: enrollmentRef.id,
        ...enrollmentData,
        enrolledAt: now,
        updatedAt: now
      };

      await setDoc(enrollmentRef, newEnrollment);
      console.log("Created new enrollment without instructor (pending assignment):", newEnrollment);
      return newEnrollment;
    }
  } catch (error) {
    console.error("Error creating/updating enrollment:", error);
    throw error;
  }
};

/**
 * Get all enrollments
 */
export const getAllEnrollments = async (): Promise<Enrollment[]> => {
  if (!db) throw new Error("Firestore is not initialized");
  
  try {
    console.log("Fetching all enrollments from Firestore collection");
    const enrollmentsRef = collection(db, "enrollments");
    const querySnapshot = await getDocs(enrollmentsRef);
    
    const enrollments: Enrollment[] = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data() as Enrollment;
      enrollments.push({ 
        id: doc.id, 
        ...data 
      } as Enrollment);
    });
    
    console.log(`Found ${enrollments.length} enrollments in Firestore`);
    return enrollments;
  } catch (error) {
    console.error("Error getting enrollments:", error);
    throw error;
  }
};

/**
 * Get enrollments by student ID
 */
export const getEnrollmentsByStudentId = async (studentId: string): Promise<Enrollment[]> => {
  if (!db) throw new Error("Firestore is not initialized");
  
  try {
    const enrollmentsRef = collection(db, "enrollments");
    // Query for both studentId and userId to handle both formats
    const studentIdQuery = query(enrollmentsRef, where("studentId", "==", studentId));
    const userIdQuery = query(enrollmentsRef, where("userId", "==", studentId));
    
    const studentIdSnapshot = await getDocs(studentIdQuery);
    const userIdSnapshot = await getDocs(userIdQuery);
    
    const enrollments: Enrollment[] = [];
    
    studentIdSnapshot.forEach((doc) => {
      enrollments.push({ id: doc.id, ...doc.data() } as Enrollment);
    });
    
    userIdSnapshot.forEach((doc) => {
      // Avoid duplicates if a document appears in both queries
      if (!enrollments.some(e => e.id === doc.id)) {
        enrollments.push({ id: doc.id, ...doc.data() } as Enrollment);
      }
    });
    
    return enrollments;
  } catch (error) {
    console.error("Error getting enrollments by student ID:", error);
    throw error;
  }
};

/**
 * Get enrollments by user ID
 */
export const getEnrollmentsByUserId = async (userId: string): Promise<Enrollment[]> => {
  if (!db) throw new Error("Firestore is not initialized");
  
  try {
    console.log("Fetching enrollments for user:", userId);
    const enrollmentsRef = collection(db, "enrollments");
    
    // Query for enrollments with broader status filter to include public bookings
    const q = query(
      enrollmentsRef, 
      where("userId", "==", userId)
      // Removed status filter to include all enrollments including public bookings
    );
    const querySnapshot = await getDocs(q);
    
    const enrollments: Enrollment[] = [];
    
    for (const enrollmentDoc of querySnapshot.docs) {
      const enrollmentData = enrollmentDoc.data();
      console.log("Processing enrollment:", enrollmentData);
      
      // Get course data to ensure we have complete information
      const courseRef = doc(db, "courses", enrollmentData.courseId);
      const courseSnap = await getDoc(courseRef);
      
      if (courseSnap.exists()) {
        const courseData = courseSnap.data();
        console.log("Fetched course data for enrollment:", courseData);
        
        // Use enrollment data values if they exist, otherwise fall back to course data
        enrollments.push({ 
          id: enrollmentDoc.id, 
          ...enrollmentData,
          courseName: enrollmentData.courseName || courseData.title || 'Unknown Course',
          totalHours: enrollmentData.totalHours || courseData.totalHours || courseData.duration || 10,
          hoursTaken: enrollmentData.hoursTaken || 0,
          hoursRemaining: enrollmentData.hoursRemaining || (enrollmentData.totalHours || courseData.totalHours || courseData.duration || 10) - (enrollmentData.hoursTaken || 0),
          amount: enrollmentData.amount || courseData.price || 0,
          // Ensure proper status mapping for public bookings
          status: enrollmentData.status || 'pending',
          enrollmentStatus: enrollmentData.enrollmentStatus || 'pending'
        } as Enrollment);
      } else {
        console.warn("Course not found for enrollment:", enrollmentData.courseId);
        // Still add the enrollment with existing data
        enrollments.push({
          id: enrollmentDoc.id,
          ...enrollmentData,
          // Ensure proper status mapping for public bookings
          status: enrollmentData.status || 'pending',
          enrollmentStatus: enrollmentData.enrollmentStatus || 'pending'
        } as Enrollment);
      }
    }
    
    console.log("Processed enrollments:", enrollments);
    return enrollments;
  } catch (error) {
    console.error("Error getting enrollments by user ID:", error);
    throw error;
  }
};

/**
 * Get enrollments by instructor ID
 */
export const getEnrollmentsByInstructorId = async (instructorId: string): Promise<Enrollment[]> => {
  if (!db) throw new Error("Firestore is not initialized");
  
  try {
    console.log("Fetching enrollments for instructor:", instructorId);
    const enrollmentsRef = collection(db, "enrollments");
    const q = query(enrollmentsRef, where("instructorId", "==", instructorId));
    const querySnapshot = await getDocs(q);
    
    const enrollments: Enrollment[] = [];
    querySnapshot.forEach((doc) => {
      enrollments.push({ id: doc.id, ...doc.data() } as Enrollment);
    });
    
    console.log(`Found ${enrollments.length} enrollments for instructor ${instructorId}`);
    return enrollments;
  } catch (error) {
    console.error("Error getting enrollments by instructor ID:", error);
    throw error;
  }
};

/**
 * Update an enrollment
 */
export const updateEnrollment = async (enrollmentId: string, data: Partial<Enrollment>): Promise<void> => {
  if (!db) throw new Error("Firestore is not initialized");

  try {
    const enrollmentRef = doc(db, "enrollments", enrollmentId);
    const enrollmentSnap = await getDoc(enrollmentRef);
    
    if (!enrollmentSnap.exists()) {
      throw new Error(`Enrollment with ID ${enrollmentId} not found`);
    }
    
    const enrollment = enrollmentSnap.data() as Enrollment;
    
    // If hoursTaken is being updated, recalculate hoursRemaining
    if (data.hoursTaken !== undefined) {
      const totalHours = enrollment.totalHours || 0;
      data.hoursRemaining = Math.max(0, totalHours - data.hoursTaken);
      console.log("Updating hours:", {
        totalHours,
        newHoursTaken: data.hoursTaken,
        newHoursRemaining: data.hoursRemaining
      });
    }
    
    // If totalHours is being updated, recalculate hoursRemaining
    if (data.totalHours !== undefined) {
      const hoursTaken = enrollment.hoursTaken || 0;
      data.hoursRemaining = Math.max(0, data.totalHours - hoursTaken);
      console.log("Updating total hours:", {
        newTotalHours: data.totalHours,
        currentHoursTaken: hoursTaken,
        newHoursRemaining: data.hoursRemaining
      });
    }
    
    // Update the enrollment
    await updateDoc(enrollmentRef, {
      ...data,
      updatedAt: new Date().toISOString()
    });
    
    console.log(`Enrollment ${enrollmentId} updated successfully`);
  } catch (error) {
    console.error("Error updating enrollment:", error);
    throw error;
  }
};

/**
 * Check enrollment status
 */
export const checkEnrollmentStatus = async (userId: string, courseId: string): Promise<{
  isEnrolled: boolean;
  enrollment?: Enrollment;
}> => {
  if (!db) throw new Error("Firestore is not initialized");

  try {
    const enrollmentsRef = collection(db, "enrollments");
    const q = query(
      enrollmentsRef,
      where("userId", "==", userId),
      where("courseId", "==", courseId)
    );
    
    const querySnapshot = await getDocs(q);
    const enrollment = querySnapshot.empty ? undefined : {
      id: querySnapshot.docs[0].id,
      ...querySnapshot.docs[0].data()
    } as Enrollment;

    return {
      isEnrolled: !querySnapshot.empty,
      enrollment
    };
  } catch (error) {
    console.error("Error checking enrollment status:", error);
    throw error;
  }
};

/**
 * Cancel an enrollment
 */
export const cancelEnrollment = async (enrollmentId: string, reason?: string): Promise<void> => {
  if (!db) throw new Error("Firestore is not initialized");

  try {
    const enrollmentRef = doc(db, "enrollments", enrollmentId);
    const enrollmentSnap = await getDoc(enrollmentRef);
    
    if (!enrollmentSnap.exists()) {
      throw new Error("Enrollment not found");
    }
    
    await updateDoc(enrollmentRef, {
      status: 'cancelled',
      enrollmentStatus: 'cancelled',
      updatedAt: new Date().toISOString(),
      cancellationReason: reason
    });

    // Create a notification for the student
    const enrollment = enrollmentSnap.data() as Enrollment;
    createNotification(
      'student',
      'enrollment-cancelled',
      'Enrollment Cancelled',
      `Your enrollment in ${enrollment.courseName} has been cancelled.${reason ? ` Reason: ${reason}` : ''}`
    );

    // If there's an assigned instructor, notify them too
    if (enrollment.instructorId) {
      createNotification(
        'instructor',
        'student-cancelled',
        'Student Cancelled Enrollment',
        `${enrollment.studentName} has cancelled their enrollment in ${enrollment.courseName}.`
      );
    }
  } catch (error) {
    console.error("Error cancelling enrollment:", error);
    throw error;
  }
};

/**
 * Get user payment history
 * @param userId User ID
 * @returns Array of payments
 */
export const getUserPayments = async (userId: string): Promise<Payment[]> => {
  if (!db) throw new Error("Firestore is not initialized");
  
  try {
    console.log("Fetching payments for user:", userId);
    const paymentsRef = collection(db, "studentPayments");
    
    // Create two separate queries
    const userIdQuery = query(
      paymentsRef,
      where("userId", "==", userId),
      orderBy("createdAt", "desc")
    );
    
    const studentIdQuery = query(
      paymentsRef,
      where("studentId", "==", userId),
      orderBy("createdAt", "desc")
    );
    
    // Execute both queries
    const [userIdSnapshot, studentIdSnapshot] = await Promise.all([
      getDocs(userIdQuery),
      getDocs(studentIdQuery)
    ]);
    
    // Combine and deduplicate results
    const allPayments = new Map();
    
    const processSnapshot = (snapshot: any, source: string) => {
      if (!snapshot || !snapshot.docs) {
        console.warn(`No valid snapshot for ${source} query`);
        return;
      }
      
      snapshot.docs.forEach((doc: any) => {
        const data = doc.data();
        // Only add if we haven't seen this payment ID before
        if (!allPayments.has(doc.id)) {
          allPayments.set(doc.id, {
        id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            matchedBy: source // Track which query found this payment
          });
        }
      });
    };
    
    // Process both snapshots
    processSnapshot(userIdSnapshot, 'userId');
    processSnapshot(studentIdSnapshot, 'studentId');
    
    const payments = Array.from(allPayments.values());
    
    console.log("Payment query results:", {
      userIdQueryCount: userIdSnapshot?.size || 0,
      studentIdQueryCount: studentIdSnapshot?.size || 0,
      totalUniquePayments: payments.length,
      payments: payments.map(p => ({
        id: p.id,
        amount: p.amount,
        status: p.status,
        courseName: p.courseName,
        matchedBy: p.matchedBy
      }))
    });
    
    return payments as Payment[];
  } catch (error) {
    console.error("Error getting user payments:", error);
    throw error;
  }
};

/**
 * Update practical lesson progress
 * @param enrollmentId Enrollment ID
 * @param hoursToAdd Number of hours to add to progress
 * @returns Updated enrollment data
 */
export const updatePracticalLessonProgress = async (
  enrollmentId: string,
  hoursToAdd: number
): Promise<Enrollment> => {
  if (!db) throw new Error("Firestore is not initialized");
  
  try {
    // Get current enrollment data
    const enrollmentRef = doc(db, "enrollments", enrollmentId);
    const enrollmentSnap = await getDoc(enrollmentRef);
    
    if (!enrollmentSnap.exists()) {
      throw new Error(`Enrollment with ID ${enrollmentId} not found`);
    }
    
    const enrollment = enrollmentSnap.data() as Enrollment;
    const currentHoursTaken = enrollment.hoursTaken || 0;
    const totalHours = enrollment.totalHours || 0;
    
    // Calculate new hours
    const newHoursTaken = currentHoursTaken + hoursToAdd;
    const newHoursRemaining = Math.max(0, totalHours - newHoursTaken);
    
    // Update enrollment
    const updateData = {
      hoursTaken: newHoursTaken,
      hoursRemaining: newHoursRemaining,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(enrollmentRef, updateData);
    
    // Return updated enrollment data
    return {
      ...enrollment,
      ...updateData,
      id: enrollmentId
    };
  } catch (error) {
    console.error("Error updating practical lesson progress:", error);
    throw error;
  }
};

/**
 * Assign an instructor to an enrollment
 * @param enrollmentId Enrollment ID
 * @param instructorId Instructor ID
 * @param instructorName Instructor name
 * @param instructorEmail Instructor email
 * @returns Updated enrollment data
 */
export const assignInstructorToEnrollment = async (
  enrollmentId: string,
  instructorId: string,
  instructorName: string,
  instructorEmail: string
): Promise<Enrollment> => {
  if (!db) throw new Error("Firestore is not initialized");
  
  try {
    const enrollmentRef = doc(db, "enrollments", enrollmentId);
    const enrollmentSnap = await getDoc(enrollmentRef);
    
    if (!enrollmentSnap.exists()) {
      throw new Error(`Enrollment with ID ${enrollmentId} not found`);
    }
    
    const enrollmentData = enrollmentSnap.data() as Enrollment;
    
    // Update enrollment with instructor details
    const updateData = {
      instructorId,
      instructorName,
      instructorEmail,
      status: 'assigned' as const,
      enrollmentStatus: 'active' as const,
      updatedAt: new Date().toISOString()
    };
    
    await updateDoc(enrollmentRef, updateData);
    
    // Create notifications
    await notifyInstructorAssignment(instructorId, enrollmentId, enrollmentData.courseName);
    await notifyStudentInstructorAssigned(enrollmentData.userId, instructorName, enrollmentId);
    
    // Return updated enrollment data
    return {
      ...enrollmentData,
      ...updateData,
      id: enrollmentId
    };
  } catch (error) {
    console.error("Error assigning instructor to enrollment:", error);
    throw error;
  }
};

export default {
  createCourse,
  getAllCourses,
  getCourseById,
  updateCourse,
  deleteCourse,
  addContentToCourse,
  deleteContentFromCourse,
  createEnrollment,
  getAllEnrollments,
  getEnrollmentsByStudentId,
  getEnrollmentsByUserId,
  getEnrollmentsByInstructorId,
  updateEnrollment,
  checkEnrollmentStatus,
  cancelEnrollment,
  getUserPayments,
  updatePracticalLessonProgress,
  assignInstructorToEnrollment,
};
